import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import { CustomMusic, CustomMusicManifest, SongSlug } from '../../../common/src/model.ts';
import { getSongSlug } from '../../../common/src/paths.ts';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { recordRecentlyAddedSong } from '../data/use_custom_music.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { useSongVisibility } from '../data/use_song_visibility.ts';
import { alert } from './confirm.tsx';
import {
  Card,
  Dialog,
  DialogCloseButton,
  DoneButton,
  FieldLabel,
  Header,
  HideInHymnalRow,
  isValidPsalmNumber,
  NameAndPsalm,
  NameField,
  onEnterKey,
  parsePsalmNumberField,
  PsalmField,
  TextField,
  Title,
  UploadProgress,
  UploadStatus,
  useCustomSongUpload,
} from './song_dialog.tsx';
import { useEffectOnce } from './use_effect_once.ts';
import { useHoldAppReload } from './use_hold_app_reload.ts';

// see addCustomSong primary entry point at bottom of file

const AddCustomSong: ConfirmDialog<
  { customMusic: CustomMusic; customMusicManifest: CustomMusicManifest | undefined; defaultSongName?: string },
  { songSlug?: SongSlug }
> = ({ customMusic, customMusicManifest, defaultSongName, proceed, show }) => (
  <AddSongDialog
    customMusic={customMusic}
    customMusicManifest={customMusicManifest}
    defaultSongName={defaultSongName}
    show={show}
    onClose={proceed}
  />
);

/**
 * The add-song pop-up (the Add Song button in Our Hymnal and the Planner's song selector): the Edit Song card
 * (GitHub issue #4071) with Name, Psalm and Hide in Our Hymnal, whose Upload button opens the file chooser for
 * the song's PDF. `onClose` gets the new song's slug once the upload is done, or nothing when the dialog is
 * dismissed. `isInline` renders it in the page flow rather than as a modal, and `initialUploadStatus` opens it
 * on an upload state, both for Storybook.
 */
export function AddSongDialog({
  customMusic,
  customMusicManifest,
  defaultSongName,
  show,
  onClose,
  isInline = false,
  initialUploadStatus,
}: {
  customMusic: CustomMusic;
  customMusicManifest: CustomMusicManifest | undefined;
  defaultSongName?: string;
  show: boolean;
  onClose: (result: { songSlug?: SongSlug }) => void;
  isInline?: boolean;
  initialUploadStatus?: UploadStatus;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (isInline) {
      return;
    }
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef, isInline]);

  // the name and psalm number live in local storage, so an interrupted attempt does not lose them
  const [songName, setSongName] = useLocalStorage(LocalStorageKey.MusicUploadSongName);
  const songNameIsValid = !songName?.includes('/');
  const [psalmNumber, setPsalmNumber] = useLocalStorage(LocalStorageKey.MusicUploadPsalmNumber);
  const psalmNumberIsValid = isValidPsalmNumber(psalmNumber);
  const songNameInputRef = useRef<HTMLInputElement>(null);
  const psalmNumberInputRef = useRef<HTMLInputElement>(null);
  const fileChooserInputRef = useRef<HTMLInputElement>(null);
  const { currentOrg } = useCurrentOrg();
  const [assignedSongNumber, setAssignedSongNumber] = useState<string | undefined>();
  const [isHiddenSong, setIsHiddenSong] = useState(false);
  const { setSongVisibility } = useSongVisibility();
  const baseId = useId();
  const ids = {
    name: `${baseId}-name`,
    psalm: `${baseId}-psalm`,
    hide: `${baseId}-hide`,
  };

  // A reload would take this dialog's unsaved name and psalm number away, so the dialog holds the app reload
  // while it is open; the upload holds it while in flight (issue #3265)
  useHoldAppReload(show);

  useEffectOnce(() => {
    setPsalmNumber(undefined); // clear previously entered psalm number
    if (!songName && defaultSongName) {
      setSongName(defaultSongName);
    }
  });

  useEffect(() => {
    songNameInputRef.current?.focus();
  }, [songNameInputRef, songName]);

  const onSongNameChanged = useCallback(() => {
    setSongName(songNameInputRef.current?.value ?? '');
  }, [songNameInputRef, setSongName]);

  const onPsalmNumberChanged = useCallback(() => {
    setPsalmNumber(parsePsalmNumberField(psalmNumberInputRef.current?.value));
  }, [psalmNumberInputRef, setPsalmNumber]);

  const onToggleHidden = useCallback(() => {
    setIsHiddenSong((isHiddenSong) => !isHiddenSong);
  }, []);

  const onClickUpload = useCallback(() => {
    if (!songName) {
      return;
    }
    if (!songNameIsValid) {
      void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
      return;
    }
    if (!psalmNumberIsValid) {
      void alert({ confirmation: <span>Invalid Psalm number</span> });
      return;
    }
    const number = songName.match(/^Psalm (?<number>\d+)/)?.groups?.number;
    if (number && !psalmNumber) {
      setPsalmNumber(Number(number));
    }
    fileChooserInputRef?.current?.click();
  }, [psalmNumber, psalmNumberIsValid, setPsalmNumber, songName, songNameIsValid]);

  // the hidden state applies once the song exists
  const onUploaded = useCallback(
    async (songNumber: string) => {
      setAssignedSongNumber(songNumber);
      if (isHiddenSong && currentOrg) {
        const songSlug = getSongSlug({ songNumber, orgId: currentOrg.id });
        await setSongVisibility({ songSlug, isVisible: false });
      }
    },
    [currentOrg, isHiddenSong, setSongVisibility],
  );

  const upload = useCustomSongUpload({
    customMusic: currentOrg && customMusic[currentOrg.id],
    currentOrg,
    songName,
    psalmNumber,
    onUploaded,
    initialStatus: initialUploadStatus,
  });

  const { setFileToUpload } = upload;
  const onFileChosen = useCallback(() => {
    const file = fileChooserInputRef?.current?.files?.[0];
    if (!file || !currentOrg || !songName) {
      console.log('no file');
      return;
    }
    setFileToUpload(file);
  }, [currentOrg, setFileToUpload, songName]);

  const onCancel = useCallback(() => {
    onClose({});
  }, [onClose]);

  const onFinish = useCallback(() => {
    if (!currentOrg || !songName) {
      if (!currentOrg) {
        console.error(`Missing org`);
      } else {
        console.error(`Missing song name`);
      }
      onClose({});
      return;
    }
    if (!assignedSongNumber) {
      console.error(`Server should have returned assigned song number`);
    }
    const songNumber = assignedSongNumber ?? '100';
    const songSlug = getSongSlug({ songNumber, orgId: currentOrg.id });
    void (async () => {
      await recordRecentlyAddedSong({
        customMusicManifest,
        org: currentOrg,
        songSlug,
        songName,
        psalmNumber,
      });
      setPsalmNumber(undefined);
      setSongName(undefined);
      onClose({ songSlug });
    })();
  }, [
    assignedSongNumber,
    currentOrg,
    customMusicManifest,
    onClose,
    psalmNumber,
    setPsalmNumber,
    setSongName,
    songName,
  ]);

  const onClickDialog = useCallback(
    (e: any) => {
      const didClickBackdrop = e.currentTarget === e.target;
      if (didClickBackdrop) {
        onCancel();
      }
    },
    [onCancel],
  );

  return (
    <Dialog ref={dialogRef} onClick={onClickDialog} open={isInline || undefined} $isInline={isInline}>
      {upload.isShowingProgress ? (
        <UploadProgress
          status={upload.status}
          errorMessage={upload.errorMessage}
          successMessage={
            isHiddenSong
              ? 'You can now add this song to events and song lists.'
              : 'The new song will be visible in your church hymnal within a couple of minutes.'
          }
          onCancel={onCancel}
          onRetry={upload.retry}
          onFinish={onFinish}
        />
      ) : (
        <Card>
          <input
            type="file"
            aria-label="Song PDF"
            style={{ display: 'none' }}
            ref={fileChooserInputRef}
            onChange={onFileChosen}
          />
          <Header>
            <Title>Add song</Title>
            <DialogCloseButton onClick={onCancel} />
          </Header>

          <NameAndPsalm>
            <NameField>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <TextField
                id={ids.name}
                type="text"
                onChange={onSongNameChanged}
                onKeyDown={onEnterKey(onClickUpload)}
                ref={songNameInputRef}
                defaultValue={songName ?? defaultSongName}
                $isInvalid={!songNameIsValid}
              />
            </NameField>
            <PsalmField>
              <FieldLabel htmlFor={ids.psalm}>Psalm</FieldLabel>
              <TextField
                id={ids.psalm}
                type="number"
                placeholder="none"
                title={psalmNumberIsValid ? undefined : 'Invalid psalm number'}
                onChange={onPsalmNumberChanged}
                onKeyDown={onEnterKey(onClickUpload)}
                defaultValue={psalmNumber}
                ref={psalmNumberInputRef}
                $isInvalid={!psalmNumberIsValid}
                $isNarrow={true}
              />
            </PsalmField>
          </NameAndPsalm>

          <HideInHymnalRow id={ids.hide} isHidden={isHiddenSong} onToggle={onToggleHidden} />

          <DoneButton type="button" onClick={onClickUpload} disabled={!songName}>
            Upload
          </DoneButton>

          <CopyrightNotice>
            By uploading music you are indicating that you have the right to distribute this music within your church.
          </CopyrightNotice>
        </Card>
      )}
    </Dialog>
  );
}

const CopyrightNotice = styled.p`
  margin: -4px 0 0;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  color: var(--color-text-label);
`;

export const addCustomSong = createConfirmation(confirmable(AddCustomSong));
